/**
 * Report-level UDA scope — the UDA dropdown every report header carries.
 *
 * Background: two modules already owned a UDA picker of their own (Employee Management
 * and Leases & Agreements), each with its own store, because a UDA chosen while reading
 * a lease report says nothing about which UDA's employees you want next. Every OTHER
 * report resolved its UDA from the JWT alone, which meant a state/DTCP admin could only
 * re-scope a report by switching the whole session's tenant in the global header.
 *
 * This is the generic picker for those reports. It writes to its own store, so it never
 * disturbs the two module-owned scopes, and it sends the pick to /reports/data as the
 * `udaId` override that the server substitutes into `$$uda_id$$`.
 *
 * Two things it deliberately does NOT do:
 *  - It never widens. The server honours the override only for a user whose JWT carries
 *    no UDA (state/regional); a UDA-scoped user stays pinned to their own UDA, so they
 *    get a read-only label here rather than a list they cannot act on.
 *  - It does not invent scoping. A report whose SQL has no `$$uda_id$$` placeholder
 *    cannot respond to the pick yet; the metadata endpoint says so via `uda_aware_in`.
 *    The dropdown still opens either way — locking it just reads as a broken control —
 *    and a small "not scoped" marker appears beside it once a UDA is picked.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuth } from '@/context/AuthContext';
import reportsServices, { type ReportUdaOption } from '../../services/reports.services';

/** Label for the un-narrowed option — the server's existing JWT-based behaviour. */
export const ALL_UDAS_LABEL = 'All UDAs';

interface ReportUdaScopeState {
  /** ds_uda_lst_t.uda_id, or null for "All UDAs". */
  udaId: number | null;
  setUdaId: (udaId: number | null) => void;
}

/**
 * Persisted so the pick survives a drill-down into a child report and a page reload —
 * a system admin reading a UDA's numbers usually reads several reports for that UDA in
 * a row. Kept separate from the auth tenant: nothing here re-issues the JWT.
 */
export const useReportUdaScopeStore = create<ReportUdaScopeState>()(
  persist(
    (set) => ({
      udaId: null,
      setUdaId: (udaId) => set({ udaId }),
    }),
    { name: 'report-uda-scope' }
  )
);

export interface ReportUdaScope {
  /** True when the acting user may re-scope at all (their JWT carries no UDA). */
  canChoose: boolean;
  /** The override to send with the report request; null = leave the server as it was. */
  udaId: number | null;
  udas: ReportUdaOption[];
  loading: boolean;
  /** The UDA a pinned (non-state) user is stuck on, for the read-only label. */
  ownLabel: string;
  setUdaId: (udaId: number | null) => void;
}

/**
 * The generic report UDA scope. Safe to call on every report — on the two module-scoped
 * routes the caller simply ignores the result in favour of that module's own store.
 */
export function useReportUdaScope(): ReportUdaScope {
  const { user: authUser } = useAuth();
  const ownUdaId = (authUser as any)?.udaId ?? null;
  const canChoose = (authUser as any)?.isStateRegionalUser === true || ownUdaId === null;

  const udaId = useReportUdaScopeStore((s) => s.udaId);
  const setUdaId = useReportUdaScopeStore((s) => s.setUdaId);

  const [udas, setUdas] = useState<ReportUdaOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canChoose) return;
    let cancelled = false;
    setLoading(true);
    reportsServices.fetchUdas()
      .then((list) => { if (!cancelled) setUdas(list); })
      .catch((error) => { if (!cancelled) { console.error('Error fetching UDAs:', error); setUdas([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canChoose]);

  const ownLabel = useMemo(() => {
    const tenants = (authUser as any)?.tenants || [];
    return (
      tenants.find((t: any) => String(t?.code) === String((authUser as any)?.tenantId))?.name ||
      udas.find((u) => u.uda_id === ownUdaId)?.uda_nm ||
      (authUser as any)?.tenantId ||
      'My UDA'
    );
  }, [authUser, udas, ownUdaId]);

  return {
    canChoose,
    // A pinned user's own UDA is already applied by the server from the JWT; sending it
    // again would be noise, so the override stays null for them.
    udaId: canChoose ? udaId : null,
    udas,
    loading,
    ownLabel,
    setUdaId,
  };
}

interface ReportUdaSelectProps {
  scope: ReportUdaScope;
  /**
   * False when the report's SQL carries no `$$uda_id$$` placeholder and therefore cannot
   * answer a pick yet. The dropdown STAYS OPENABLE either way — a control that will not
   * open reads as broken — and says so in a marker beside it instead.
   */
  udaAware?: boolean;
  className?: string;
  /** 'dark' renders the translucent h-7 version for a module's dark header bar. */
  tone?: 'light' | 'dark';
}

/** The dropdown itself, sized for the report header strip. */
export function ReportUdaSelect({ scope, udaAware = true, className, tone = 'light' }: ReportUdaSelectProps) {
  const dark = tone === 'dark';
  const { canChoose, udaId, udas, loading, ownLabel, setUdaId } = scope;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setUdaId(e.target.value === '' ? null : Number(e.target.value));
    },
    [setUdaId]
  );

  if (!canChoose) {
    return (
      <span
        className={dark
          ? 'inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-white/10 ring-1 ring-inset ring-white/20 text-white text-[11px] font-semibold'
          : 'inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 bg-white text-gray-700 text-[11px] font-medium dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200'}
        title="Your UDA"
      >
        <i className={dark ? 'ri-building-2-line text-white/70' : 'ri-building-2-line text-gray-400'}></i>
        <span className="truncate max-w-[120px]">{ownLabel}</span>
      </span>
    );
  }

  const title = 'Run this report for one UDA';

  return (
    <div className={className ?? 'flex items-center gap-1.5'}>
      <span className={dark ? 'text-[10px] font-semibold uppercase tracking-wide text-white/70' : 'text-[11px] font-medium text-gray-500 dark:text-gray-400'}>UDA</span>
      <div className="relative">
        <select
          value={udaId ?? ''}
          disabled={loading}
          onChange={handleChange}
          title={title}
          className={dark
            ? 'appearance-none w-[160px] h-7 truncate rounded-md bg-white/10 ring-1 ring-inset ring-white/20 pl-2 pr-6 text-xs font-semibold text-white outline-none transition-colors hover:bg-white/20 focus:ring-white/50 disabled:cursor-not-allowed disabled:opacity-60 [&>option]:text-gray-900'
            : 'appearance-none w-[140px] truncate rounded border border-gray-200 bg-white pl-2 pr-5 py-0.5 text-[11px] font-medium text-gray-700 outline-none transition-colors hover:border-gray-300 focus:border-blue-400 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:disabled:bg-gray-900'}
        >
          <option value="">{loading ? 'Loading UDAs...' : ALL_UDAS_LABEL}</option>
          {udas.map((u) => (
            <option key={u.uda_id} value={u.uda_id}>{u.uda_nm}</option>
          ))}
        </select>
        <i className={`ri-arrow-down-s-line pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-xs ${dark ? 'text-white/70' : 'text-gray-400'}`}></i>
      </div>
      {/* The report cannot act on the pick yet — say it here rather than by locking the
          control, which just looks like a bug. */}
      {!udaAware && udaId !== null && (
        <span
          className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
          title="This report's query is not UDA-scoped yet, so the rows below are unfiltered."
        >
          <i className="ri-error-warning-line"></i>
          not scoped
        </span>
      )}
    </div>
  );
}
